<template>

  <v-container fluid>
      <v-row>
          <v-col>
            <loading-spinner v-if="isLoading"></loading-spinner>

            <!-- 필터 영역 -->
            <v-card v-if="!isLoading" class="mb-4">
                <v-card-text>
                    <v-row align="center">
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="selectedJournalIdx"
                                :items="journalOptions"
                                item-text="text"
                                item-value="value"
                                label="그룹 필터 (간행물)"
                                clearable
                                outlined
                                dense
                                @change="onJournalFilterChange"
                            ></v-select>
                        </v-col>
                        <v-col cols="12" md="auto">
                            <v-btn
                                color="info"
                                outlined
                                @click="openHowToModal"
                            >
                                <v-icon left>help_outline</v-icon>
                                심사위원 배정 사용법
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- 페이징 테이블 -->
            <paginated-table v-if="!isLoading" :key="tableKey" :data="paginatedPapers" :columns="tableColumns" :title="tableTitle"
                @updatePagination="updatePagination"></paginated-table>

            <reviewer-select-form v-if="showModal && isReviewerLoaded" :columns="modalColumns" v-model="reviewers"
                @close="controlModal" @send-data="handleAssignReviewer"
                :preSelectedItems="currentAssignedReviewers">
            </reviewer-select-form>

            <!-- 사용법 모달 -->
            <v-dialog v-model="showHowToModal" max-width="1200px">
                <v-card>
                    <v-card-title class="headline">
                        심사위원 배정 사용법
                        <v-spacer></v-spacer>
                        <v-btn icon @click="closeHowToModal">
                            <v-icon>close</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <v-img
                            src="/img/how/howToReviewers01.png"
                            contain
                            max-height="800"
                        ></v-img>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" text @click="closeHowToModal">
                            닫기
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-col>
    </v-row>
  </v-container>
</template>

<script>
    import PaginatedTable from '@/components/Tables/PaginatedTable.vue';
    import ReviewerSelectForm from '@/components/specific/Reviewer/ReviewerSelectForm.vue';
    import LoadingSpinner from '@/components/common/Spinners/LoadingSpinner.vue';

    import { getAssignmentColumns, getSelectReviewerColumns } from '@/views/ReviewerView/columns';

    import { reviewerApi, paperApi, reviewApi, finalReviewApi, journalApi } from '@/api/api';

    export default {
        name: "reviewers-assignment",
        components: {
            PaginatedTable,
            ReviewerSelectForm,
            LoadingSpinner,
        },
        data() {
            return {
                // labels
                tableTitle: "심사위원 배정",

                // fields & columns
                modalColumns: getSelectReviewerColumns({}),
                tableColumns: getAssignmentColumns({
                    "assign": this.openModal,
                    "delete": this.handleDeleteReviewers,
                }),

                // boolean
                showModal: false,
                showHowToModal: false,
                isLoading: false,
                isReviewerLoaded: false,

                // filter
                selectedJournalIdx: null,
                journalOptions: [],
                tableKey: 0,

                // pagination
                currentPage: 1,
                currentSize: 10,

                // init data
                currentPaperIdx: null,
                currentAssignedReviewers: [],
                currentReviewIdxs: [],
                rawPapers: [],
                reviewers: [],
            }
        },
        computed: {
            /**
             * Journal 필터가 적용된 논문 목록
             */
            filteredPapers() {
                if (!this.selectedJournalIdx) {
                    // 필터가 선택되지 않은 경우 전체 데이터 반환
                    return this.rawPapers;
                }

                // selectedJournalIdx로 필터링
                return this.rawPapers.filter(paper => {
                    return paper.journal && paper.journal.idx === this.selectedJournalIdx;
                });
            },

            /**
             * 페이지네이션이 적용된 논문 목록 (PaginatedTable에 전달)
             */
            paginatedPapers() {
                const start = (this.currentPage - 1) * this.currentSize;
                const end = start + this.currentSize;
                const elements = this.filteredPapers.slice(start, end);

                return {
                    elements: elements,
                    page_info: {
                        page: this.currentPage,
                        size: this.currentSize,
                        total_elements: this.filteredPapers.length,
                        total_pages: Math.ceil(this.filteredPapers.length / this.currentSize) || 1
                    }
                };
            }
        },
        methods: {
            /**
             * 모달 컨트롤
             */
            async openModal(paper) {
                this.currentPaperIdx = paper.idx;
                await this.fetchReviewers();

                // 논문 객체에 포함된 reviewers 배열에서 배정된 심사위원 정보 추출
                if (paper.reviewers && paper.reviewers.length > 0) {
                    this.currentReviewIdxs = paper.reviewers.map(reviewer => reviewer.review_idx);
                    this.currentAssignedReviewers = paper.reviewers.map(reviewer => ({
                        idx: reviewer.reviewer_idx,
                        name: reviewer.reviewer_name,
                        email: reviewer.reviewer_email,
                        organization: reviewer.organization,
                        department: reviewer.department,
                        position: reviewer.position,
                        started_at: reviewer.assigned_date,
                        ended_at: reviewer.review_deadline,
                    }));
                } else {
                    // 배정된 심사위원이 없는 경우 빈 배열
                    this.currentReviewIdxs = [];
                    this.currentAssignedReviewers = [];
                }

                this.showModal = true;
            },

            controlModal() {
                this.showModal = false;
                this.currentPaperIdx = null;
                this.currentAssignedReviewers = [];
                this.currentReviewIdxs = [];
            },

            /**
             * 사용법 모달 컨트롤
             */
            openHowToModal() {
                this.showHowToModal = true;
            },

            closeHowToModal() {
                this.showHowToModal = false;
            },

            /**
             * Pagination 타입 명세
             * @typedef {Object} Conditions
             * @property {string} page - 페이지
             * @property {string} size - 사이즈
             * @property {string} total_elements - 전체 데이터 수 
             * @property {string} total_pages - 전체 페이지 수
             */

            /**
             * 페이지네이션 업데이트 메서드
             *
             * @description: 페이징 정보가 변경되면 로컬 상태 업데이트 (API 재호출 없음)
             * @params {Pagination} pagination
             */
            updatePagination(pagination) {
                this.currentSize = pagination.size;
                this.currentPage = pagination.page;
                // computed property (paginatedPapers)가 자동으로 재계산됨
            },


            /**
             * *****************************************************************************************
             * 버튼 액션
             * *****************************************************************************************
             */

            /**
             * 심사위원 배정/수정 처리
             * @param data - 선택된 심사위원 및 기간 정보
             */
            async handleAssignReviewer(data) {
                // 최초 배정 여부를 먼저 저장
                const isFirstAssignment = this.currentReviewIdxs.length === 0;

                // 기존 심사위원이 있으면 삭제
                if (!isFirstAssignment) {
                    const deleteSuccess = await this.deleteExistingReviewers();
                    if (!deleteSuccess) {
                        alert('기존 심사위원 삭제에 실패했습니다.');
                        return;
                    }
                }

                // 새로운 심사위원 배정
                const reviewSuccess = await this.createReview(data);
                if (!reviewSuccess) {
                    alert('심사위원 배정에 실패했습니다.');
                    return;
                }

                // 최초 배정인 경우에만 총평 생성
                if (isFirstAssignment) {
                    const finalReviewSuccess = await this.createFinalReview();
                    if (!finalReviewSuccess) {
                        alert('총평 생성에 실패했습니다.');
                        return;
                    }
                }

                alert('심사위원이 성공적으로 배정되었습니다.');
                this.controlModal();
                await this.fetchPapers();
            },

            /**
             * 심사위원 전체 삭제 처리
             * @param paper - 논문 객체
             */
            async handleDeleteReviewers(paper) {
                // 배정된 심사위원이 없으면 삭제 불가
                if (!paper.reviewers || paper.reviewers.length === 0) {
                    alert('배정된 심사위원이 없습니다.');
                    return;
                }

                // 삭제 확인
                if (!confirm(`${paper.title}\n\n배정된 심사위원 ${paper.reviewers.length}명을 모두 삭제하시겠습니까?`)) {
                    return;
                }

                try {
                    // 배정된 모든 심사위원 삭제
                    for (const reviewer of paper.reviewers) {
                        await reviewApi.deleteReview({ idx: reviewer.review_idx });
                    }

                    alert('심사위원이 모두 삭제되었습니다.');
                    await this.fetchPapers();
                } catch (error) {
                    alert('심사위원 삭제에 실패했습니다.');
                }
            },

            /**
             * *****************************************************************************************
             * API 요청
             * *****************************************************************************************
             */

            /**
             * 필터 변경 핸들러
             */
            onJournalFilterChange() {
                // 필터 변경 시 첫 페이지로 이동
                this.currentPage = 1;
                // 테이블 key를 변경하여 강제 재렌더링
                this.tableKey++;
            },

            /**
             * 심사위원 배정 가능한 논문 목록 조회 API 요청 메서드 (전체 데이터 로드)
             */
            async fetchPapers() {
                const params = {
                    size: 1000, // 전체 데이터 로드
                    page: 1,
                    sort_by: "idx",
                    direction: "desc",
                };

                this.isLoading = true;
                try {
                    const response = await paperApi.getPendingReviewPapers(params);

                    if (response && response.status === 200 && response.data && response.data.response) {
                        // 각 논문에 대해 심사위원 수 레이블 추가
                        const elements = response.data.response.elements || [];
                        this.rawPapers = elements.map(paper => ({
                            ...paper,
                            reviewer_count_label: paper.reviewers && paper.reviewers.length > 0
                                ? `${paper.reviewers.length}명`
                                : '미배정'
                        }));
                    } else {
                        // API 응답이 정상이 아닌 경우 빈 배열
                        this.rawPapers = [];
                    }
                } catch {
                    // 에러 발생 시 빈 배열
                    this.rawPapers = [];
                } finally {
                    this.isLoading = false;
                }
            },

            /**
             * 간행물(Journal) 목록 조회 API 요청 메서드
             */
            async fetchJournals() {
                try {
                    const response = await journalApi.showAll({
                        page: 1,
                        size: 1000, // 전체 목록 조회
                        sort_by: "idx",
                        direction: "desc",
                    });

                    if (response && response.status === 200 && response.data && response.data.response) {
                        const journals = response.data.response.elements || [];
                        // 드롭다운 옵션 생성
                        this.journalOptions = [
                            { text: '전체', value: null },
                            ...journals.map(journal => ({
                                text: `${journal.title} (${journal.publication_date || '발행일 미정'})`,
                                value: journal.idx
                            }))
                        ];
                    } else {
                        // API 응답이 정상이 아닌 경우 기본 옵션만 설정
                        this.journalOptions = [{ text: '전체', value: null }];
                    }
                } catch {
                    // 에러 발생 시 기본 옵션만 설정
                    this.journalOptions = [{ text: '전체', value: null }];
                }
            },


            /**
             * 리뷰어 목록 조회 API 요청 메서드
             */
            async fetchReviewers() {
                this.isReviewerLoaded = false;

                try {
                    const response = await reviewerApi.getNonPaginatedReviewers();

                    if (response && response.status === 200 && response.data && response.data.response) {
                        this.reviewers = response.data.response || [];
                    } else {
                        this.reviewers = [];
                    }
                } catch {
                    this.reviewers = [];
                } finally {
                    this.isReviewerLoaded = true;
                }
            },

            /**
             * 할당 API 요청 메서드
             */
            async createReview(data) {
                try {
                    const reviewerIdxs =
                        data.selectedItems && Array.isArray(data.selectedItems)
                            ? data.selectedItems.map(item => item.idx)
                            : [];

                    const request = {
                        reviewer_idxs: reviewerIdxs,
                        paper_idx: this.currentPaperIdx,
                        assigned_date: data.date.startedAt,
                        review_deadline: data.date.endedAt,
                    }
                    const response = await reviewApi.createReview(request);

                    if (response && response.status === 201) {
                        return true;
                    } else {
                        return false;
                    }
                }
                catch {
                    return false;
                }
            },

            async createFinalReview() {
                try {
                    const request = {
                        paper_idx: this.currentPaperIdx,
                    }
                    const response = await finalReviewApi.createFinalReview(request);

                    if (response && response.status === 201) {
                        return true;
                    } else {
                        return false;
                    }
                }
                catch {
                    return false;
                }
            },

            /**
             * 기존 심사위원 삭제 API 요청 메서드
             */
            async deleteExistingReviewers() {
                try {
                    for (const reviewIdx of this.currentReviewIdxs) {
                        await reviewApi.deleteReview({ idx: reviewIdx });
                    }
                    return true;
                } catch {
                    return false;
                }
            },
        },
        mounted() {
            this.fetchJournals();
            this.fetchPapers();
        },
    }
</script>

<style lang="sass" scoped>

</style>